import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { fetchRegion, formatPrice } from '@/lib/region';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
    Sparkles, Stars, Clock, ArrowRight, Wallet,
    History as HistoryIcon, User, MessageCircle, X, Loader2,
    Heart,
} from 'lucide-react';
import { RelationBadge } from '@/components/ProfilePicker';
import DashboardServicesPanel from '@/components/DashboardServicesPanel';

// Verdict badge colour palette
const VERDICT_STYLES = {
    YES:            'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
    NO:             'bg-rose-500/10 text-rose-400 border-rose-500/30',
    YES_WITH_DELAY: 'bg-amber-500/10 text-amber-300 border-amber-500/30',
    NO_BUT_LATER:   'bg-sky-500/10 text-sky-400 border-sky-500/30',
    YES_BUT_DELAY:  'bg-amber-500/10 text-amber-300 border-amber-500/30',
    // Compatibility v2 4-tier labels
    'Made for Each Other':   'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
    'A Good Match':          'bg-marigold/15 text-marigold border-marigold/40',
    'Has Its Challenges':    'bg-amber-500/10 text-amber-300 border-amber-500/30',
    'Needs Careful Thought': 'bg-rose-500/10 text-rose-300 border-rose-500/30',
    // Compatibility v1 fallback labels (lowercase from `verdict`)
    highly_compatible:       'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
    compatible:              'bg-marigold/15 text-marigold border-marigold/40',
    partially_compatible:    'bg-amber-500/10 text-amber-300 border-amber-500/30',
    uncertain:               'bg-slate-500/10 text-slate-300 border-slate-500/30',
    incompatible:            'bg-rose-500/10 text-rose-300 border-rose-500/30',
};

const STATUS_LABEL = {
    active:   'In progress',
    ended:    'Ended',
    timeout:  'Timed out',
    abandoned: 'Abandoned',
};

export default function Dashboard() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [sessions, setSessions] = useState(null);   // null → loading; merged timeline of chats + compatibility readings
    const [walletInr, setWalletInr] = useState(null);
    const [openSessionId, setOpenSessionId] = useState(null);
    // Round 40n — currency-aware wallet card + chat rate copy.
    const [currency, setCurrency] = useState('INR');

    useEffect(() => {
        // Round 44 — Merge chat sessions and compatibility readings into
        // a single, time-sorted timeline. Each row carries a `kind`
        // discriminator ('chat' | 'compatibility') so the renderer knows
        // which deep-link / drawer to open.
        Promise.allSettled([
            api.get('/chat/history'),
            api.get('/compatibility/history'),
        ]).then(([chatRes, compatRes]) => {
            const chatRows = chatRes.status === 'fulfilled' ? (chatRes.value.data || []) : [];
            const compatRows = compatRes.status === 'fulfilled' ? (compatRes.value.data?.checks || []) : [];

            const chats = chatRows.map((c) => ({
                ...c,
                kind: 'chat',
                __sort: c.started_at || c.created_at || '',
            }));
            const compats = compatRows.map((c) => {
                const v2Label = c?.narration && typeof c.narration === 'object'
                    ? c.narration.verdict_label
                    : null;
                return {
                    ...c,
                    kind: 'compatibility',
                    verdict_label: v2Label || c.verdict,
                    __sort: c.created_at || '',
                };
            });
            const merged = [...chats, ...compats].sort((a, b) =>
                (b.__sort || '').localeCompare(a.__sort || '')
            );
            setSessions(merged);
        });
        api.get('/wallet/balance')
            .then((r) => setWalletInr(r.data?.balance_inr ?? 0))
            .catch(() => setWalletInr(0));
        fetchRegion()
            .then((r) => setCurrency(r?.currency || 'INR'))
            .catch(() => { /* default INR */ });
    }, []);

    // Landing-page-honest chat rate per currency.
    const chatRate = currency === 'USD' ? '$1 / minute' : '₹25 / minute';

    return (
        <div className="max-w-6xl mx-auto px-6 py-10 md:py-16" data-testid="dashboard">
            {/* Greeting hero */}
            <div className="mb-10">
                <div className="overline mb-3">
                    {greet()}, {user?.name?.split(' ')[0]}
                </div>
                <h1 className="font-serif text-4xl md:text-5xl text-parchment">
                    What does the sky <em className="text-marigold not-italic">hold for you today?</em>
                </h1>
            </div>

            {/* Top action cards */}
            <div className="grid lg:grid-cols-3 gap-6 mb-12">
                <Card className="lg:col-span-2 bg-elevated border-marigold/15 rounded-sm p-8 relative overflow-hidden" data-testid="dashboard-ask-card">
                    <div className="absolute -right-20 -top-20 w-64 h-64 rounded-full bg-marigold/5 blur-3xl" />
                    <MessageCircle className="text-marigold mb-4" size={22} />
                    <h2 className="font-serif text-3xl text-parchment mb-2">Chat with Guruji</h2>
                    <p className="text-sm text-slate-400 mb-6 max-w-md">
                        A live conversation with your KP-trained astrologer. {chatRate} · pause anytime.
                    </p>
                    <Button
                        onClick={() => navigate('/app/chat')}
                        className="bg-marigold text-obsidian hover:bg-marigold-hover rounded-none h-11 px-6"
                        data-testid="dashboard-start-ask"
                    >
                        Begin chat <ArrowRight size={16} className="ml-2" />
                    </Button>
                </Card>

                <Card className="bg-surface border-marigold/15 rounded-sm p-6" data-testid="dashboard-wallet-card">
                    <div className="flex items-center justify-between mb-5">
                        <Wallet className="text-marigold" size={22} />
                        <span className="overline text-copper">Wallet</span>
                    </div>
                    <div className="font-serif text-5xl text-parchment" data-testid="dashboard-wallet-balance">
                        {walletInr === null ? '…' : formatPrice(walletInr, currency)}
                    </div>
                    <div className="text-sm text-slate-500 mt-1">{chatRate} · pause anytime</div>
                    <Link to="/app/credits" className="block mt-6">
                        <Button variant="outline" className="w-full bg-transparent border-marigold/40 text-parchment hover:bg-marigold/10 rounded-none h-10" data-testid="dashboard-buy-credits">
                            Recharge wallet
                        </Button>
                    </Link>
                </Card>
            </div>

            {/* Services panel — feature-discovery strip (Compatibility,
                Birth-time analysis, Expert consultation, …). Extensible
                via the SERVICES array in DashboardServicesPanel.jsx. */}
            <DashboardServicesPanel />

            {/* Readings list (merged from former /app/history) */}
            <section data-testid="dashboard-history-section">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="font-serif text-2xl text-parchment flex items-center gap-2">
                        <HistoryIcon size={20} className="text-marigold" /> Your readings
                    </h2>
                    {sessions && sessions.length > 0 && (
                        <span className="text-xs text-slate-500 uppercase tracking-wider">
                            {sessions.length} total
                        </span>
                    )}
                </div>

                {sessions === null && (
                    <div className="text-slate-500 text-sm" data-testid="dashboard-history-loading">Loading…</div>
                )}

                {sessions && sessions.length === 0 && (
                    <Card className="bg-surface border-marigold/10 rounded-sm p-12 text-center" data-testid="dashboard-empty">
                        <Stars className="text-marigold/50 mx-auto mb-4" size={32} />
                        <p className="text-slate-400 mb-2">No chats yet.</p>
                        <Link to="/app/chat" className="text-marigold hover:text-marigold-50">
                            Start your first chat with Guruji →
                        </Link>
                    </Card>
                )}

                <div className="space-y-4" data-testid="dashboard-history-list">
                    {sessions?.map((s) => (
                        s.kind === 'compatibility'
                            ? <CompatibilityRow key={`compat-${s.id}`} item={s} currency={currency} onOpen={() => navigate(`/app/compatibility?check_id=${s.id}`)} />
                            : <ChatRow key={`chat-${s.id}`} item={s} currency={currency} onOpen={() => setOpenSessionId(s.id)} />
                    ))}
                </div>
            </section>

            {openSessionId && (
                <SessionDrawer
                    sessionId={openSessionId}
                    onClose={() => setOpenSessionId(null)}
                />
            )}
        </div>
    );
}

function ChatRow({ item: s, currency, onOpen }) {
    return (
        <button
            type="button"
            onClick={onOpen}
            data-testid={`history-item-${s.id}`}
            className="w-full text-left"
        >
            <Card className="bg-surface border-marigold/10 rounded-sm p-6 glow-hover flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-3 mb-2">
                        <Badge className="rounded-sm border bg-marigold/10 text-marigold border-marigold/30 inline-flex items-center gap-1">
                            <MessageCircle size={10} /> Chat
                        </Badge>
                        {s.verdict_label && (
                            <Badge className={`rounded-sm border ${VERDICT_STYLES[s.verdict_label] || 'bg-slate-500/10 text-slate-300 border-slate-500/30'}`}>
                                {String(s.verdict_label).replace(/_/g, ' ')}
                            </Badge>
                        )}
                        {s.verdict_category && (
                            <span className="text-xs text-slate-500 uppercase tracking-wider">{s.verdict_category}</span>
                        )}
                        {s.horary_number && (
                            <>
                                <span className="text-xs text-slate-600">·</span>
                                <span className="text-xs text-slate-500">horary #{s.horary_number}</span>
                            </>
                        )}
                        {s.profile_name && (
                            <>
                                <span className="text-xs text-slate-600">·</span>
                                <span className="text-xs text-slate-400 inline-flex items-center gap-1.5">
                                    <User size={11} className="text-marigold" />
                                    for {s.profile_name}
                                </span>
                            </>
                        )}
                        <span className="text-xs text-slate-600">·</span>
                        <span className="text-xs text-slate-500">{STATUS_LABEL[s.status] || s.status}</span>
                    </div>
                    <p className="font-serif text-lg md:text-xl text-parchment leading-snug line-clamp-2">
                        {s.first_question || '(no question recorded)'}
                    </p>
                    <div className="mt-2 text-xs text-slate-500 flex flex-wrap items-center gap-3">
                        <span className="inline-flex items-center gap-1"><Clock size={11} />{new Date(s.started_at).toLocaleString()}</span>
                        {s.tick_count ? <span>· {s.tick_count} min</span> : null}
                        {s.total_deducted_inr ? <span>· {formatPrice(Number(s.total_deducted_inr), currency)}</span> : null}
                    </div>
                </div>
                <div className="text-xs text-marigold inline-flex items-center gap-1 shrink-0">
                    View chat <ArrowRight size={12} />
                </div>
            </Card>
        </button>
    );
}

function CompatibilityRow({ item: s, currency, onOpen }) {
    const personA = s.profile_name || 'You';
    const personB = s.partner_name || 'Partner';
    const v2Label = s?.narration && typeof s.narration === 'object'
        ? s.narration.verdict_label
        : null;
    const label = v2Label || (s.verdict ? String(s.verdict).replace(/_/g, ' ') : null);
    const badgeKey = v2Label || s.verdict;
    return (
        <button
            type="button"
            onClick={onOpen}
            data-testid={`history-compat-${s.id}`}
            className="w-full text-left"
        >
            <Card className="bg-surface border-marigold/10 rounded-sm p-6 glow-hover flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-3 mb-2">
                        <Badge className="rounded-sm border bg-rose-500/10 text-rose-300 border-rose-500/30 inline-flex items-center gap-1">
                            <Heart size={10} /> Compatibility
                        </Badge>
                        {label && (
                            <Badge className={`rounded-sm border capitalize ${VERDICT_STYLES[badgeKey] || 'bg-slate-500/10 text-slate-300 border-slate-500/30'}`}>
                                {label}
                            </Badge>
                        )}
                        {s.profile_name && (
                            <>
                                <span className="text-xs text-slate-600">·</span>
                                <span className="text-xs text-slate-400 inline-flex items-center gap-1.5">
                                    <User size={11} className="text-marigold" />
                                    {s.profile_name}
                                </span>
                            </>
                        )}
                    </div>
                    <p className="font-serif text-lg md:text-xl text-parchment leading-snug line-clamp-2">
                        {personA} <span className="text-marigold/60 mx-1">&</span> {personB}
                    </p>
                    <div className="mt-2 text-xs text-slate-500 flex flex-wrap items-center gap-3">
                        <span className="inline-flex items-center gap-1"><Clock size={11} />{new Date(s.created_at).toLocaleString()}</span>
                        {s.amount_charged_inr != null ? <span>· {formatPrice(Number(s.amount_charged_inr), currency)}</span> : null}
                    </div>
                </div>
                <div className="text-xs text-marigold inline-flex items-center gap-1 shrink-0">
                    View reading <ArrowRight size={12} />
                </div>
            </Card>
        </button>
    );
}

function SessionDrawer({ sessionId, onClose }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        api.get(`/chat/session/${sessionId}`)
            .then((r) => { if (alive) setData(r.data); })
            .catch(() => { if (alive) setData(null); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [sessionId]);

    return (
        <div
            className="fixed inset-0 z-50 bg-obsidian/70 backdrop-blur-sm flex justify-end"
            onClick={onClose}
            data-testid="session-drawer-overlay"
        >
            <div
                className="w-full max-w-xl bg-surface border-l border-marigold/15 h-full overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
                data-testid="session-drawer"
            >
                <div className="sticky top-0 bg-surface/95 backdrop-blur border-b border-marigold/10 px-5 py-4 flex items-center justify-between z-10">
                    <div>
                        <div className="text-xs text-marigold uppercase tracking-wider">Conversation with Guruji</div>
                        <div className="text-sm text-parchment line-clamp-1">{data?.session?.first_question || '…'}</div>
                    </div>
                    <button
                        onClick={onClose}
                        data-testid="session-drawer-close"
                        className="p-2 rounded-sm hover:bg-marigold/10 text-slate-400 hover:text-parchment"
                    >
                        <X size={18} />
                    </button>
                </div>
                {loading && (
                    <div className="p-10 flex items-center justify-center text-marigold">
                        <Loader2 className="animate-spin" size={20} />
                    </div>
                )}
                {!loading && data && (
                    <div className="px-5 py-4 space-y-3">
                        {(data.messages || []).map((m) => <SessionBubble key={m.id} m={m} />)}
                        {(data.messages || []).length === 0 && (
                            <div className="text-slate-500 italic text-sm">No messages saved for this session.</div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

function SessionBubble({ m }) {
    const isUser = m.role === 'user';
    const c = m.content || {};
    const bubbles = Array.isArray(c.bubbles) ? c.bubbles : null;
    const chips = Array.isArray(c.chips) ? c.chips : null;
    const text = c.text || c.reply || '';
    return (
        <div className={isUser ? 'flex justify-end' : 'flex justify-start'}>
            <div className={`max-w-[88%] rounded-sm p-3 text-sm whitespace-pre-wrap ${
                isUser
                    ? 'bg-marigold/15 text-parchment border border-marigold/30'
                    : 'bg-elevated text-slate-200 border border-marigold/10'
            }`}>
                {text && <div>{text}</div>}
                {bubbles && bubbles.map((b, i) => (
                    <div key={i} className={i > 0 ? 'mt-2 pt-2 border-t border-marigold/10' : ''}>{b}</div>
                ))}
                {chips && chips.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                        {chips.map((chip, i) => (
                            <span key={i} className="px-2 py-0.5 rounded-full bg-marigold/10 text-marigold text-[10px] border border-marigold/20">{chip}</span>
                        ))}
                    </div>
                )}
                <div className="mt-1.5 text-[10px] text-slate-500">{new Date(m.created_at).toLocaleString()}</div>
            </div>
        </div>
    );
}

function greet() {
    const h = new Date().getHours();
    if (h < 5)  return 'Still awake';
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    if (h < 21) return 'Good evening';
    return 'Good night';
}
